import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiTrendingUp } from 'react-icons/fi';
const SERIES = [
  { key: 'approved', label: 'Approved', color: '#10B981' },
  { key: 'pending',  label: 'Pending',  color: '#F59E0B' },
  { key: 'rejected', label: 'Rejected', color: '#EF4444' },
];

const GROUPS = [
  { key: 'month',   label: 'Month' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'year',    label: 'Year' },
];

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

// Stacked SVG columns: approved / pending / rejected per period,
// with a month / quarter / year group-by toggle.
// Clicking a column drills into that period's records.
const WaiverTrendChart = ({ data, loading, error, groupBy, onGroupByChange, onDrillDown }) => {
  const [tooltip, setTooltip] = useState(null);
  const rows = data || [];
  const maxTotal = Math.max(...rows.map(r => r.total), 1);

  const chartH = 160, barW = 34, gap = 56;
  const width = Math.max(rows.length * gap + 20, 300);

  return (
    <Card padding="none" className="tw-border tw-border-gray-200 tw-shadow-sm">
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiTrendingUp size={15} className="tw-text-primary-500" />
          <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">Waiver Trend</h3>
          <div className="tw-flex tw-items-center tw-gap-3 tw-ml-3">
            {SERIES.map(s => (
              <span key={s.key} className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-gray-500">
                <span className="tw-w-2 tw-h-2 tw-rounded-sm" style={{ background: s.color }} />{s.label}
              </span>
            ))}
          </div>
        </div>
        <div className="tw-flex tw-rounded tw-overflow-hidden tw-border tw-border-gray-200">
          {GROUPS.map(g => (
            <button key={g.key} type="button" onClick={() => onGroupByChange(g.key)}
              className={`tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-transition-colors tw-cursor-pointer tw-border-0 ${
                groupBy === g.key ? 'tw-bg-primary-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'
              }`}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="md" /></div>
      ) : error || rows.length === 0 ? (
        <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-10">No data for this period</p>
      ) : (
        <div className="tw-p-5 tw-overflow-x-auto tw-relative">
          <svg width={width} height={chartH + 50}>
            {rows.map((r, i) => {
              const x = i * gap + 16;
              let yCursor = chartH;
              const segments = SERIES.map(s => {
                const h = (r[s.key] / maxTotal) * chartH;
                yCursor -= h;
                return { ...s, h, y: yCursor, value: r[s.key] };
              });
              return (
                <g key={r.period}
                  onMouseEnter={() => setTooltip({ i, r })}
                  onMouseLeave={() => setTooltip(null)}
                  onClick={() => onDrillDown && onDrillDown(r, groupBy)}
                  style={onDrillDown ? { cursor: 'pointer' } : undefined}>
                  {segments.map(seg => seg.h > 0 && (
                    <rect key={seg.key} x={x} y={seg.y} width={barW} height={seg.h}
                      fill={seg.color} opacity={0.85} rx={2}>
                      <title>{`${r.label} — ${seg.label}: ${fmt(seg.value)}`}</title>
                    </rect>
                  ))}
                  <text x={x + barW / 2} y={yCursor - 5} textAnchor="middle" fontSize="10" fontWeight="700" className="tw-fill-gray-700">
                    {fmt(r.total)}
                  </text>
                  <text x={x + barW / 2} y={chartH + 16} textAnchor="middle" fontSize="9" className="tw-fill-gray-400">
                    {r.label}
                  </text>
                  {r.overdue > 0 && (
                    <text x={x + barW / 2} y={chartH + 30} textAnchor="middle" fontSize="9" fontWeight="600" fill="#F97316">
                      {fmt(r.overdue)} ovd
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          {tooltip && (
            <div className="tw-absolute tw-top-2 tw-right-3 tw-bg-slate-800 tw-text-white tw-rounded tw-px-3 tw-py-2 tw-text-[11px] tw-shadow-lg tw-pointer-events-none">
              <p className="tw-font-bold tw-mb-1">{tooltip.r.label}</p>
              <p className="tw-mb-0">Total: {fmt(tooltip.r.total)} · Approved: {fmt(tooltip.r.approved)}</p>
              <p className="tw-mb-0">Pending: {fmt(tooltip.r.pending)} · Rejected: {fmt(tooltip.r.rejected)} · Overdue: {fmt(tooltip.r.overdue)}</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

WaiverTrendChart.propTypes = {
  data: PropTypes.array, loading: PropTypes.bool, error: PropTypes.any,
  groupBy: PropTypes.string.isRequired, onGroupByChange: PropTypes.func.isRequired,
  onDrillDown: PropTypes.func,
};
WaiverTrendChart.defaultProps = { data: [], loading: false, error: null, onDrillDown: null };

export default WaiverTrendChart;
